import React, { useState, useEffect, useRef, useMemo } from "react";
import { getDocs, doc, getDoc, collection, updateDoc, onSnapshot, query, where, runTransaction, collectionGroup } from "firebase/firestore";
import { useLocation } from 'react-router-dom';
import { setDoc } from "firebase/firestore";
import { db } from "../../firebase";
import DateRangePopup from "../../components/DateRangePopup";
import { 
  getCountryCode, 
  getRegionList,
  getDistrictList,
  getMunicipalityList 
} from "../../utils/regionHelper";

const SMMEsOptions = [
  {
    "heading": "Smallholder Commercial Farms",
    "items": [
      {
        "label": "Small-scale farms operating for profit. Use improved inputs, some mechanization, and sell surplus to local markets. Often family-run but may hire seasonal labor. Example: A 5-hectare maize farm using hybrid seeds and selling to a local mill.",
        "value": "ESC"
      }
    ]
  },
  {
    "heading": "Medium-Scale Farms",
    "items": [
      {
        "label": "Larger landholding than smallholders (e.g., 10–100 hectares). Often mechanized and more capitalized. Supply supermarkets, processors, or export markets. Example: A 50-hectare rice farm with irrigation and a tractor, selling to a national rice brand.",
        "value": "EMS"
      }
    ]
  },
  {
    "heading": "Farmer Cooperatives / Producer Organizations",
    "items": [
      {
        "label": "Groups of farmers who pool resources to gain market power. May own equipment, storage, or transport collectively. Can register as SMEs and operate commercially. Example: A cooperative of 100 tomato farmers owning a cold storage unit.",
        "value": "EFC"
      }
    ]
  },
  {
    "heading": "Contract Farming SMEs",
    "items": [
      {
        "label": "Farmers or farm groups under formal agreements to supply crops/livestock to processors or exporters. Often receive inputs and training from buyers. Example: Poultry farmers supplying eggs to a food company under a contract.",
        "value": "ECF"
      }
    ]
  },
  {
    "heading": "Niche or Specialized Farms",
    "items": [
      {
        "label": "Farms focused on high-value or specialized production: Organic farms, Horticulture farms (vegetables, flowers, herbs), Aquaculture enterprises, Livestock or poultry SMEs, Beekeeping and honey production. Example: A small farm producing organic herbs for export.",
        "value": "ENS"
      }
    ]
  },
  {
    "heading": "Agri-Entrepreneurs with Integrated Operations",
    "items": [
      {
        "label": "These SMEs farm and also manage part of the value chain, such as: On-farm processing (e.g., dairy to yogurt), Direct-to-consumer sales (farm shops, online orders). Example: A mushroom farmer selling pre-packed mushrooms to urban grocers.",
        "value": "EAE"
      }
    ]
  },
  {
    "heading": "Youth or Women-led Farming Startups",
    "items": [
      {
        "label": "Often tech-savvy and innovative. Use climate-smart or digital farming tools. May focus on urban/peri-urban agriculture. Example: A woman-led hydroponic vegetable farm near a city.",
        "value": "EYW"
      }
    ]
  },
]
const getRegionMap = (country) => {
  const countryMappings = {
    'South Africa': {
      "Gauteng": "01",
      "Eastern Cape": "02",
      "Limpopo": "03",
      "KwaZulu-Natal": "04",
      "Mpumalanga": "05",
      "Western Cape": "06",
      "North West": "07",
      "Northern Cape": "08",
      "Free State": "09"
    },
    'Kenya': {
      "Mombasa": "1",
      "Kwale": "2",
      "Kilifi": "3",
      "Tana River": "4",
      "Lamu": "5",
      "Taita Taveta": "6",
      "Garissa": "7",
      "Wajir": "8",
      "Mandera": "9",
      "Marsabit": "10",
      "Isiolo": "11",
      "Meru": "12",
      "Tharaka-Nithi": "13",
      "Embu": "14",
      "Kitui": "15",
      "Machakos": "16",
      "Makueni": "17",
      "Nyandarua": "18",
      "Nyeri": "19",
      "Kirinyaga": "20",
      "Murang'a": "21",
      "Kiambu": "22",
      "Turkana": "23",
      "West Pokot": "24",
      "Samburu": "25",
      "Trans Nzoia": "26",
      "Uasin Gishu": "27",
      "Elgeyo-Marakwet": "28",
      "Nandi": "29",
      "Baringo": "30",
      "Laikipia": "31",
      "Nakuru": "32",
      "Narok": "33",
      "Kajiado": "34",
      "Kericho": "35",
      "Bomet": "36",
      "Kakamega": "37",
      "Vihiga": "38",
      "Bungoma": "39",
      "Busia": "40",
      "Siaya": "41",
      "Kisumu": "42",
      "Homa Bay": "43",
      "Migori": "44",
      "Kisii": "45",
      "Nyamira": "46",
      "Nairobi City": "47"
    },
    'Namibia': {
      "Erongo": "08",
      "Hardap": "12",
      "Kavango East": "06",
      "Kavango West": "14",
      "Khomas": "11",
      "Kunene": "01",
      "Ohangwena": "04",
      "Omaheke": "10",
      "Omusati": "02",
      "Oshana": "03",
      "Oshikoto": "05",
      "Otjozondjupa": "09",
      "Zambezi": "07",
      "Karas": "013"
    },
    'Zambia': {
      "Western": "1",
      "Central": "2",
      "Eastern": "3",
      "Luapula": "4",
      "Northern": "5",
      "North-Western": "6",
      "Southern": "7",
      "Copperbelt": "8",
      "Lusaka": "9",
      "Muchinga": "10"
    },
    'Burundi': {
      "Buhumuza": "01",
      "Bujumbura": "02",
      "Burunga": "03",
      "Butanyerera": "04",
      "Gitega": "05"
    },
    'Rwanda': {
      "City of Kigali": "01",
      "Eastern": "02",
      "Northern": "03",
      "Southern": "04",
      "Western": "05"
    },
    'Uganda': {
      "Northern": "01",
      "Eastern": "02",
      "Central": "03",
      "Western": "04"
    },
    'Malawi': {
      "Northern": "01",
      "Central": "02",
      "Southern": "03"
    },
    'Angola': {
      "Bengo": "01",
      "Benguela": "02",
      "Bié": "03",
      "Cabinda": "04",
      "Cuando Cubango": "05",
      "Cuanza Norte": "07",
      "Cuanza Sul": "08",
      "Cunene": "09",
      "Huambo": "10",
      "Huíla": "11",
      "Luanda": "13",
      "Lunda Norte": "14",
      "Lunda Sul": "15",
      "Malanje": "16",
      "Moxico": "17",
      "Namibe": "19",
      "Uíge": "20",
      "Zaire": "21"
    },
    'Burkina Faso': {
      "Boucle du Mouhoun": "01",
      "Cascades": "02",
      "Centre": "03",
      "Centre-Est": "04",
      "Centre-Nord": "05",
      "Centre-Ouest": "06",
      "Centre-Sud": "07",
      "Est": "08",
      "Hauts-Bassins": "09",
      "Nord": "10",
      "Plateau-Central": "11",
      "Sahel": "12",
      "Sud-Ouest": "13"
    },
    'Eswatini': {
      "Hhohho": "01",
      "Manzini": "02",
      "Lubombo": "03",
      "Shiselweni": "04"
    },
    'Lesotho': {
      "Berea": "01",
      "Butha-Buthe": "02",
      "Leribe": "03",
      "Mafeteng": "04",
      "Maseru": "05",
      "Mohale's Hoek": "06",
      "Mokhotlong": "07",
      "Qacha's Nek": "08",
      "Quthing": "09",
      "Thaba-Tseka": "10"
    },
    'Tanzania': {
      "Arusha": "01",
      "Dar es Salaam": "02",
      "Dodoma": "03",
      "Geita": "04",
      "Iringa": "05",
      "Kagera": "06",
      "Katavi": "07",
      "Kigoma": "08",
      "Kilimanjaro": "09",
      "Lindi": "10",
      "Manyara": "11",
      "Mara": "12",
      "Mbeya": "13",
      "Mjini Magharibi (Zanzibar Urban/West)": "14",
      "Morogoro": "15",
      "Mtwara": "16",
      "Mwanza": "17",
      "Njombe": "18",
      "Pemba North": "19",
      "Pemba South": "20",
      "Pwani (Coast)": "21",
      "Rukwa": "22",
      "Ruvuma": "23",
      "Shinyanga": "24",
      "Simiyu": "25",
      "Singida": "26",
      "Songwe": "27",
      "Tabora": "28",
      "Tanga": "29"
    },
    'Cameroon': {
      "Adamawa": "01",
      "Centre": "02",
      "East": "03",
      "Far North": "04",
      "Littoral": "05",
      "North": "06",
      "North-West": "07",
      "West": "08",
      "South": "09",
      "South-West": "10"
    },
'Democratic Republic of the Congo': {
  "Bas-Uele": "01",
  "Equateur": "02",
  "Haut-Katanga": "03",
  "Haut-Lomami": "04",
  "Haut-Uele": "05",
  "Ituri": "06",
  "Kasai": "07",
  "Kasai-Central": "08",
  "Kasai-Oriental": "09",
  "Kinshasa": "10",
  "Kongo-Central": "11",
  "Kwango": "12",
  "Kwilu": "13",
  "Lomami": "14",
  "Lualaba": "15",
  "Mai-Ndombe": "16",
  "Maniema": "17",
  "Mongala": "18",
  "Nord-Kivu": "19",
  "Nord-Ubangi": "20",
  "Sankuru": "21",
  "Sud-Kivu": "22",
  "Sud-Ubangi": "23",
  "Tanganyika": "24",
  "Tshopo": "25",
  "Tshuapa": "26"
},
'Congo (Kinshasa/DRC)': { // Add this as an alias
  "Bas-Uele": "01",
  "Equateur": "02",
  "Haut-Katanga": "03",
  "Haut-Lomami": "04",
  "Haut-Uele": "05",
  "Ituri": "06",
  "Kasai": "07",
  "Kasai-Central": "08",
  "Kasai-Oriental": "09",
  "Kinshasa": "10",
  "Kongo-Central": "11",
  "Kwango": "12",
  "Kwilu": "13",
  "Lomami": "14",
  "Lualaba": "15",
  "Mai-Ndombe": "16",
  "Maniema": "17",
  "Mongala": "18",
  "Nord-Kivu": "19",
  "Nord-Ubangi": "20",
  "Sankuru": "21",
  "Sud-Kivu": "22",
  "Sud-Ubangi": "23",
  "Tanganyika": "24",
  "Tshopo": "25",
  "Tshuapa": "26"
},
    'Ethiopia': {
      "Addis Ababa": "01",
      "Afar": "02",
      "Amhara": "03",
      "Benishangul-Gumuz": "04",
      "Dire Dawa": "05",
      "Gambela": "06",
      "Harari": "07",
      "Oromia": "08",
      "Sidama": "09",
      "Somali": "10",
      "Southern Nations (SNNPR)": "11",
      "Tigray": "12"
    },
  'Côte d\'Ivoire (Ivory Coast)': {
      "Abidjan": "1",
      "Yamoussoukro": "2",
      "Bas-Sassandra": "3",
      "Comoé": "4",
      "Denguélé": "5",
      "Gôh-Djiboua": "6",
      "Lacs": "7",
      "Lagunes": "8",
      "Montagnes": "9",
      "Sassandra-Marahoué": "10",
      "Savanes": "11",
      "Vallée du Bandama": "12",
      "Woroba": "13",
      "Zanzan": "14"
    },
    'Ghana': {
      "WESTERN": "1",
      "CENTRAL": "2",
      "GREATER ACCRA": "3",
      "VOLTA": "4",
      "EASTERN": "5",
      "ASHANTI": "6",
      "WESTERN NORTH": "7",
      "AHAFO": "8",
      "BONO": "9",
      "BONO EAST": "10",
      "OTI": "11",
      "NORTHERN": "12",
      "SAVANNAH": "13",
      "UPPER WEST": "14",
      "NORTH EAST": "15",
      "UPPER EAST": "16"
    },
    'Mozambique': {
      "Cabo Delgado": "1",
      "Gaza": "2",
      "Inhambane": "3",
      "Manica": "4",
      "Maputo City": "5",
      "Maputo": "6",
      "Nampula": "7",
      "Niassa": "8",
      "Sofala": "9",
      "Tete": "10",
      "Zambezia": "11"
    },
    'Nigeria': {
      "North Central (NC)": "1",
      "North East (NE)": "2",
      "North West (NW)": "3",
      "South East (SE)": "4",
      "South South (SS)": "5",
      "South West (SW)": "6"
    },
    // ... other country mappings ...
    'Senegal': {
      "Dakar": "01",
      "Diourbel": "02",
      "Fatick": "03",
      "Kaffrine": "04",
      "Kaolack": "05",
      "Kedougou": "06",
      "Kolda": "07",
      "Louga": "08",
      "Matam": "09",
      "Saint-Louis": "10",
      "Sedhiou": "11",
      "Tambacounda": "12",
      "Thies": "13",
      "Ziguinchor": "14"
    },
  };
 // Handle case where country might be "Congo (Kinshasa/DRC)"
 const normalizedCountry = country === "Congo (Kinshasa/DRC)" 
 ? "Democratic Republic of the Congo" 
 : country;

if (!countryMappings[normalizedCountry]) {
 return {
   "-": "No regions available"
 };
}

  const generatePlaceholders = (count) => {
    const placeholders = {};
    for (let i = 1; i <= count; i++) {
      placeholders[`-${i}`] = `Region data unavailable`;
    }
    return placeholders;
  };

  return {
    ...countryMappings[country],
    ...generatePlaceholders(9) // Generate 9 placeholder options
  };
  
};

const EnterpriseSection = ({ regions, country,onTotalCalculated  }) => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [totalRegistrations, setTotalRegistrations] = useState(0);
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const selectedCountry = queryParams.get('country') || country?.name || 'South Africa';
  const [currentChunkTotal, setCurrentChunkTotal] = useState(0);
 // Modify the data fetching to properly filter by current selections
const [reportSelections, setReportSelections] = useState({
  COUNTRY: "-",
  REGION: "-",
  DISTRICT: "-",
  MUNICIPALITY: "-",
  SMMEs: "-", // Changed from SMMEsOptions[0].items[0]
  "VALUE CHAIN": "-",
  PROJECT: "-",
  "USER TYPE": "-" // Changed from "View more"
});
const [filteredTotal, setFilteredTotal] = useState(0);
const updateStats = async (country, region, smmeType, landSize) => {
  const statsRef = doc(db, "stats", "registrations");
  
  try {
    await runTransaction(db, async (transaction) => {
      const statsDoc = await transaction.get(statsRef);
      let statsData = statsDoc.exists() ? statsDoc.data() : {};
      
      // Initialize country if not exists
      if (!statsData[country]) {
        statsData[country] = {};
      }
      
      // Initialize region if not exists
      if (!statsData[country][region]) {
        statsData[country][region] = {
          total: 0,
          landSizeTotal: 0,
          bySmmetype: {}
        };
      }
      
      // Initialize SMME type if not exists
      if (!statsData[country][region].bySmmetype[smmeType]) {
        statsData[country][region].bySmmetype[smmeType] = {
          count: 0,
          landSizeTotal: 0
        };
      }
      
      // Update counts - INCREMENT rather than set
      statsData[country][region].total += 1;
      statsData[country][region].landSizeTotal += landSize;
      statsData[country][region].bySmmetype[smmeType].count += 1;
      statsData[country][region].bySmmetype[smmeType].landSizeTotal += landSize;
      
      transaction.set(statsRef, statsData);
    });
  } catch (error) {
    console.error("Transaction failed:", error);
  }
};
useEffect(() => {
  const unsubscribe = onSnapshot(doc(db, "registrations", "current_selection"), (doc) => {
    if (doc.exists()) {
      const selection = doc.data();
      const region = getRegionName(selection.REGION, selection.COUNTRY || country);
      const smmeType = selection.SMMEs?.value || selection.SMMEs;
      const landSize = (selection.landData?.type1?.size || 0) + (selection.landData?.type2?.size || 0);
      
      if (region && smmeType) {
        setReportData(prev => {
          const newData = {...prev};
          
          // Initialize if not exists
          if (!newData[region]) {
            newData[region] = {
              total: 0,
              landSizeTotal: 0,
              bySmmetype: {}
            };
          }
          
          if (!newData[region].bySmmetype[smmeType]) {
            newData[region].bySmmetype[smmeType] = {
              count: 0,
              landSizeTotal: 0
            };
          }
          
          // Increment counts
          newData[region].total += 1;
          newData[region].landSizeTotal += landSize;
          newData[region].bySmmetype[smmeType].count += 1;
          newData[region].bySmmetype[smmeType].landSizeTotal += landSize;
          
          return newData;
        });
        
        // Also update the stats document
        updateStats(selection.COUNTRY || country, region, smmeType, landSize);
      }
    }
  });

  return () => unsubscribe();
}, [country]);

  // Get the complete region map for the selected country
  const fullRegionMap = getRegionMap(selectedCountry);
  
  // Use all available regions from the region map
  const allRegions = Object.keys(fullRegionMap)
    .filter(region => !region.startsWith('-')); // Exclude placeholder regions

  // Split regions into chunks of 9 for display
  const chunkSize = 9;
  const regionChunks = [];

  for (let i = 0; i < allRegions.length; i += chunkSize) {
    const chunk = allRegions.slice(i, i + chunkSize);
    // If chunk has fewer than 9 items, fill with placeholders
    while (chunk.length < chunkSize) {
      chunk.push(`-`); // Add placeholder like "-1", "-2", etc.
    }
    regionChunks.push(chunk);
  }

  const getRegionName = (regionData, country) => {
    if (!regionData) return null;
    
    // Get the country name properly
    const countryName = country?.name || country || 'South Africa';
    const regionMap = getRegionMap(countryName);
    
    // Handle string input
    if (typeof regionData === 'string') {
      // Try exact match first
      if (regionMap[regionData]) return regionData;
      
      // Try case-insensitive match
      const foundKey = Object.keys(regionMap).find(
        key => key.toLowerCase() === regionData.toLowerCase()
      );
      
      // Try matching by code if name not found
      if (!foundKey) {
        const foundByCode = Object.entries(regionMap).find(
          ([name, code]) => code === regionData
        );
        return foundByCode ? foundByCode[0] : null;
      }
      
      return foundKey || null;
    }
      // Handle DRC specifically
  if (country === 'Democratic Republic of the Congo' || country === 'Congo (Kinshasa/DRC)') {
    if (typeof regionData === 'string') {
      // Try to match with or without accents
      const normalizedInput = regionData.normalize("NFD").replace(/[\u0300-\u036f]/g, "");
      const foundRegion = Object.keys(getRegionMap('Democratic Republic of the Congo')).find(
        region => region.normalize("NFD").replace(/[\u0300-\u036f]/g, "") === normalizedInput
      );
      return foundRegion || null;
    }
    // Handle other cases (object with value/name)...
  }
    // Handle object with value property
    if (regionData.value) {
      // First try matching the value as a code
      const foundByCode = Object.entries(regionMap).find(
        ([name, code]) => code === regionData.value.toString()
      );
      
      if (foundByCode) return foundByCode[0];
      
      // Fallback to matching as a name
      return Object.keys(regionMap).find(
        key => regionMap[key] === regionData.value.toString()
      );
    }
    
    // Handle object with name property
    if (regionData.name) {
      // Try exact match first
      if (regionMap[regionData.name]) return regionData.name;
      
      // Try case-insensitive match
      const foundKey = Object.keys(regionMap).find(
        key => key.toLowerCase() === regionData.name.toLowerCase()
      );
      return foundKey || null;
    }
    
    return null;
  };


  const getSmmetype = (smmeData) => {
    if (!smmeData) return null;
    if (typeof smmeData === 'string') return smmeData;
    return smmeData.value || null;
  };

  const getCount = (region, key, field = 'count') => {
    if (!reportData || !reportData[region]) return 0;
    
    if (field === 'landSizeTotal') {
      if (!key) return reportData[region][field] || 0;
      return reportData[region].bySmmetype[key]?.[field] || 0;
    }
    
    if (!key) return reportData[region][field] || 0;
    return reportData[region].bySmmetype[key]?.[field] || 0;
  };

  const getTotalForAllRegions = (key, field = 'count', currentChunkRegions) => {
    if (!reportData) return 0;
    
    let total = 0;
    currentChunkRegions.forEach(region => {
      if (key) {
        total += reportData[region]?.bySmmetype[key]?.[field] || 0;
      } else {
        total += reportData[region]?.[field] || 0;
      }
    });
    return total;
  };


  useEffect(() => {
    const fetchData = async (selection) => {
      try {
        setLoading(true);
        
        // First try to get from stats collection
        const statsDoc = await getDoc(doc(db, "stats", "registrations"));
        if (statsDoc.exists()) {
          setReportData(statsDoc.data());
          setLoading(false);
          return;
        }
  
        // Fallback to calculating from registrations
        const registrationsSnapshot = await getDocs(collection(db, "registrations"));
        
        const data = {};
        const allRegions = Object.keys(getRegionMap(selectedCountry));
        
        allRegions.forEach(region => {
          data[region] = {
            total: 0,
            landSizeTotal: 0,
            bySmmetype: {}
          };
               // Query registrations for the specific country
        const q = query(
          collection(db, "registrations"),
          where("country", "==", country.name)
        );
          SMMEsOptions.forEach(category => {
            category.items.forEach(item => {
              data[region].bySmmetype[item.value] = {
                count: 0,
                landSizeTotal: 0
              };
            });
          });
        });
  
        registrationsSnapshot.forEach(doc => {
          const regData = doc.data();
          const regionName = getRegionName(regData.REGION, selectedCountry);
          
          if (!regionName || !data[regionName]) return;
  
          const landSize = 
            (regData.landData?.type1?.size || 0) + 
            (regData.landData?.type2?.size || 0);
  
          const smmeType = regData.SMMEs?.value || regData.SMMEs;
          
          // Update totals
          data[regionName].total += 1;
          data[regionName].landSizeTotal += landSize;
  
          // Update by SMME type if available
          if (smmeType) {
            if (!data[regionName].bySmmetype[smmeType]) {
              data[regionName].bySmmetype[smmeType] = {
                count: 0,
                landSizeTotal: 0
              };
            }
            
            data[regionName].bySmmetype[smmeType].count += 1;
            data[regionName].bySmmetype[smmeType].landSizeTotal += landSize;
          }
        });
  
        setReportData(data);
      } catch (error) {
        console.error("Error fetching reports:", error);
        setError("Failed to load report data");
      } finally {
        setLoading(false);
      }
    };
  

    fetchData();
  }, [selectedCountry]); // Add selectedCountry as dependency


  useEffect(() => {
    const unsubscribe = onSnapshot(doc(db, "registrations", "current_selection"), (doc) => {
      if (doc.exists()) {
        const selection = doc.data();
        const region = getRegionName(selection.REGION, selection.COUNTRY || country);
        const smmeType = selection.SMMEs?.value || selection.SMMEs;
        
        if (region && smmeType) {
          setReportData(prev => {
            const newData = {...prev};
            
            // Initialize if not exists
            if (!newData[region]) {
              newData[region] = {
                total: 0,
                landSizeTotal: 0,
                bySmmetype: {}
              };
            }
            
            if (!newData[region].bySmmetype[smmeType]) {
              newData[region].bySmmetype[smmeType] = {
                count: 0,
                landSizeTotal: 0
              };
            }
            
            // Increment counts
            newData[region].total += 1;
            newData[region].bySmmetype[smmeType].count += 1;
            
            return newData;
          });
        }
      }
    });

    return () => unsubscribe();
  }, [country]);

useEffect(() => {
  const unsubscribe = onSnapshot(doc(db, "registrations", "current_selection"), (doc) => {
    if (doc.exists()) {
      const selection = doc.data();
      setReportSelections(prev => ({
        ...prev,
        ...selection
      }));
      
      // Process the region data
      const region = getRegionName(selection.REGION, selection.COUNTRY || country);
      const smmeType = selection.SMMEs?.value || selection.SMMEs;
      
      if (region && smmeType) {
        setReportData(prev => {
          const newData = {...prev};
          
          // Initialize if not exists
          if (!newData[region]) {
            newData[region] = {
              total: 0,
              landSizeTotal: 0,
              bySmmetype: {}
            };
          }
          
          if (!newData[region].bySmmetype[smmeType]) {
            newData[region].bySmmetype[smmeType] = {
              count: 0,
              landSizeTotal: 0
            };
          }
          
          // Increment counts
          newData[region].total += 1;
          newData[region].bySmmetype[smmeType].count += 1;
          
          return newData;
        });
      }
    }
  });

  return () => unsubscribe();
}, [country]);
const totalEntrepreneurs = useMemo(() => {
  if (!reportData) return 0;
  return Object.keys(reportData).reduce((sum, region) => {
    return sum + (reportData[region]?.total || 0);
  }, 0);
}, [reportData]);

// Update parent when total changes
useEffect(() => {
  onTotalCalculated(totalEntrepreneurs);
}, [totalEntrepreneurs, onTotalCalculated]);
// Modify fetchData to use the current selections
// In EnterpriseSection component, modify the data processing
const fetchData = async () => {
  try {
    setLoading(true);
    
    const q = query(
      collection(db, "registrations"),
      where("COUNTRY", "==", selectedCountry)
    );
    
    const registrationsSnapshot = await getDocs(q);
    
    const data = {};
    const allRegions = Object.keys(getRegionMap(selectedCountry));
    
    // Initialize all regions
    allRegions.forEach(region => {
      data[region] = {
        total: 0,
        landSizeTotal: 0,
        bySmmetype: {}
      };
      
      // Initialize all SMME types
      SMMEsOptions.forEach(category => {
        category.items.forEach(item => {
          data[region].bySmmetype[item.value] = {
            count: 0,
            landSizeTotal: 0
          };
        });
      });
    });

    // Process each registration
    registrationsSnapshot.forEach(doc => {
      const regData = doc.data();
      const regionName = getRegionName(regData.REGION, selectedCountry);
      
      if (!regionName || !data[regionName]) return;

      const landSize = 
        (regData.landData?.type1?.size || 0) + 
        (regData.landData?.type2?.size || 0);

      const smmeType = regData.SMMEs?.value || regData.SMMEs;
      
      // Update totals - INCREMENT counts
      data[regionName].total += 1;
      data[regionName].landSizeTotal += landSize;

      // Update by SMME type if available
      if (smmeType) {
        if (!data[regionName].bySmmetype[smmeType]) {
          data[regionName].bySmmetype[smmeType] = {
            count: 0,
            landSizeTotal: 0
          };
        }
        
        data[regionName].bySmmetype[smmeType].count += 1;
        data[regionName].bySmmetype[smmeType].landSizeTotal += landSize;
      }
    });

    setReportData(data);
  } catch (error) {
    console.error("Error fetching reports:", error);
    setError("Failed to load report data");
  } finally {
    setLoading(false);
  }
};
useEffect(() => {
  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Create query based on current selections
      let queryConstraints = [];
      
      if (reportSelections.COUNTRY) {
        queryConstraints.push(where("COUNTRY", "==", reportSelections.COUNTRY));
      }
      
      if (reportSelections.REGION && reportSelections.REGION !== "-") {
        queryConstraints.push(where("REGION", "==", reportSelections.REGION));
      }
      
      if (reportSelections.SMMEs && reportSelections.SMMEs !== "-") {
        const smmeValue = typeof reportSelections.SMMEs === 'object' ? 
          reportSelections.SMMEs.value : reportSelections.SMMEs;
        queryConstraints.push(where("SMMEs", "==", smmeValue));
      }
      
      const q = query(collection(db, "registrations"), ...queryConstraints);
      await getDocs(q); // Removed unused variable querySnapshot
      
      // Process the data as before...
      
    } catch (error) {
      console.error("Error fetching data:", error);
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  };
  
  fetchData();
}, [reportSelections]); // Re-run when selections change
// In EnterpriseSection, modify the total calculation
useEffect(() => {
  if (reportSelections.REGION && reportSelections.REGION !== "-") {
    const regionName = getRegionName(reportSelections.REGION, reportSelections.COUNTRY);
    const regionData = reportData?.[regionName];
    setFilteredTotal(regionData?.total || 0);
  } else {
    // Calculate total for the selected country only
    const countryTotal = Object.keys(reportData || {}).reduce((sum, region) => {
      return sum + (reportData[region]?.total || 0);
    }, 0);
    setFilteredTotal(countryTotal);
  }
}, [reportSelections.REGION, reportSelections.COUNTRY, reportData]);

// Pass filteredTotal to parent
useEffect(() => {
  onTotalCalculated(filteredTotal);
}, [filteredTotal, onTotalCalculated]);

useEffect(() => {
  if (reportData) {
    const total = regionChunks.reduce((total, chunk) => {
      return total + chunk.reduce((chunkTotal, region) => {
        return chunkTotal + (reportData[region]?.total || 0);
      }, 0);
    }, 0);
    setCurrentChunkTotal(total);
    onTotalCalculated(total); // Pass to parent if needed
  }
}, [reportData, regionChunks]);

useEffect(() => {
  if (reportData) {
    const total = Object.values(reportData).reduce((sum, region) => {
      return sum + (region?.total || 0);
    }, 0);
    setTotalRegistrations(total);
    onTotalCalculated(total);
  }
}, [reportData]);
  if (loading) return <div>Loading report data...</div>;
  if (error) return <div>Error: {error}</div>;
  if (!reportData) return <div>No report data available</div>;

  const sections = [
    { 
      title: "",
      items: [
        {label: 'Production Cost', key: 'SMF', },
        {label: '-', key: 'SMF', },
      ]
    },
    {
      title: "",
      items: [
        {label: 'Smallholder Commercial Farms (ESC)', key: 'ESC', color: '#152C24'},
        {label: 'Land size (HA)', key: 'ESC', field: 'landSizeTotal', color: '#152C24'},
      ]
    },
    {
      title: "",
      items: [
        {label: 'Medium-Scale Farms (EMS)', key: 'EMS', color: '#2E453D'},
        {label: 'Land size (HA)',key: 'EMS', color: '#2E453D'},
      ]
    },
    {
      title: "",
      items: [
        {label: 'Farmer Cooperatives/Producer Organizations (EFC)', key: 'EFC', color: '#8EC543'},
        {label: 'Land size (HA)', key: 'EFC', color: '#8EC543'},
      ]
    },
    {
      title: "",
      items: [
        {label: 'Contract Farming SMEs (ECF)', key: 'ECF', color: '#C4C4C4'},
        {label: 'Land size (HA)',  key: 'ECF', color: '#C4C4C4'},
      ]
    },
    {
      title: "",
      items: [
        {label: 'Niche or Specialized Farms (ENS)', key: 'ENS', color: '#F23939'},
        {label: 'Land size (HA)', key: 'ENS', color: '#F23939'},
      ]
    },
    {
      title: "",
      items: [
        {label: 'Agri-Entrepreneurs with Integrated Operations (EAE)', key: 'EAE', color: '#FFBD26'},
        {label: 'Land size (HA)', key: 'EAE', color: '#FFBD26'},
      ]
    },
    {
      title: "",
      items: [
        {label: 'Youth or Women-led Farming Startups (EYW)', key: 'EYW', color: '#000000'}, // Repeating first color if needed
        {label: 'Land size (HA)', key: 'EYW', color: '#000000'},
      ]
    },
  ];
  

  return (
    <div className="mb-6 md:mt-10 mt-3">
   {(() => {
  const totalEntrepreneurs = regionChunks.reduce((total, chunk) => {
    return total + chunk.reduce((chunkTotal, region) => {
      return chunkTotal + (reportData[region]?.total || 0);
    }, 0);
  }, 0);
    
    // Pass the total to parent component
    return (
      <div className="hidden" data-total={totalEntrepreneurs}></div>
    );
  })()}
      {/* Render each chunk of regions as a separate section */}
      {regionChunks.map((regionChunk, chunkIndex) => (
        <div key={`chunk-${chunkIndex}`} className="mb-8">
          {/* Region Headers */}
          <div className="flex md:flex-row items-start gap-1 md:gap-2">
            <div className="w-[90px] md:w-[220px]">
              <p className="h-4 md:h-7 text-[#152C24] rounded-md text-[5px] md:text-[16px] font-bold">AGRICULTURAL SMMEs</p>
            </div>
            <p className="h-4 md:h-7 text-[#152C24] rounded-md text-[4px] md:text-[16px] font-bold">TOTAL</p>

            <div className="grid grid-cols-9 md:grid-cols-9 md:gap-[10px] gap-[20px] flex-1">
              {regionChunk.map((region, i) => (
                <div key={i} className="text-[4px] md:text-[14px] rounded-[5px] text-black font-extrabold px-1 text-center md:w-[100px]">
                  {region}
                </div>
              ))}
            </div>
          </div>
          
          <p className="text-[#152C24] font-bold mb-2 text-[10px] md:text-[14px]">TOTAL</p>
          <div className="mt-2">
            <div className='md:h-[1.5px] h-[1px] bg-black'></div>

            <div className="overflow-x-auto">
              <div className="md:w-auto grid md:grid-cols-[300px_repeat(9,1fr)] grid-cols-[120px_repeat(9,1fr)] gap-y-1 gap-x-1 md:gap-x-2 mt-2 md:mt-3">
                <div className="flex items-center gap-1">
                  <div className="text-[6px] md:text-[14px] text-black font-bold leading-tight">
                    Agricultural Entrepreneurs
                  </div>
                  <div className="h-4 md:h-7 text-[#152C24] md:ml-[65px] ml-[25px] rounded-md text-[5px] md:text-[14px] flex items-center justify-center font-normal">
  {getTotalForAllRegions(null, 'total', regionChunk)}
</div>

                </div>
                {regionChunk.map((region, j) => (
                  <div
                    key={`total-${j}`}
                    className="h-4 md:h-7 bg-[#CFD7D7] rounded-md text-[5px] md:text-[16px] flex items-center justify-center font-bold"
                  >
                    {getCount(region, null, 'total')}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Land Size Totals Row */}
       {/* Land Size Totals Row */}
<div className="">
  <div className="overflow-x-auto">
    <div className="md:w-auto grid md:grid-cols-[300px_repeat(9,1fr)] grid-cols-[120px_repeat(9,1fr)] gap-y-1 gap-x-1 md:gap-x-2 mt-1 md:mt-2">
      <div className="flex items-center gap-1">
        <div className="text-[6px] md:text-[14px] text-black font-normal leading-tight">
          Land size (HA)
        </div>
        {/* Grand Total */}
        <div className="h-4 md:h-7 text-[#152C24] ml-[60px] rounded-md md:ml-[150px] text-[5px] md:text-[14px] flex items-center justify-center font-normal">
          {regionChunk.reduce((total, region) => {
            return total + (reportData[region]?.landSizeTotal || 0);
          }, 0)}
        </div>
      </div>
      {/* Region Totals - Now showing landSizeTotal instead of summing landSizeCount */}
      {regionChunk.map((region) => (
        <div 
          key={`total-${region}`}
          className="h-4 md:h-7 bg-[#CFD7D7] rounded-md text-[5px] md:text-[16px] flex items-center justify-center font-normal"
        >
          {reportData[region]?.landSizeTotal || 0}
        </div>
      ))}
    </div>
  </div>
</div>

          {/* Dynamic Sections */}

{sections.map((section, index) => (
  <div key={index} className="mt-2">
    <p className="text-[#152C24] font-bold mb-2 text-[10px] md:text-[14px]">
      {section.title}
    </p>
    <div className='md:h-[1.5px] h-[1px] bg-black'></div>

    <div className="overflow-x-auto">
      <div className="md:w-auto grid md:grid-cols-[200px_90px_repeat(9,1fr)] grid-cols-[90px_25px_repeat(9,1fr)] gap-y-1 gap-x-1 md:gap-x-2 mt-2 md:mt-3">
        {section.items.map((item, i) => (
          <React.Fragment key={`${section.title}-${item.key}-${i}`}>
            <div className="flex items-center gap-5  w-[300px]">
              <div className={`text-[6px] md:text-[14px] w-[160px] text-black leading-tight ${i === 0 ? 'font-bold' : 'font-normal'}`}>
                {item.label}
              </div>
              {/* Only show colored circle for enterprise rows (first item in each section) */}
              {i === 0 && item.color && (
                <div 
                  className="w-[6px] h-[6px] md:w-[36px] md:h-[36px] rounded-full mr-1 md:mr-2"
                  style={{ backgroundColor: item.color }}
                ></div>
              )}
            </div>
            <div className="h-4 md:h-7 text-[#152C24] rounded-md text-[5px] md:text-[14px] flex items-center justify-center font-normal">
              {getTotalForAllRegions(item.key, item.field || 'count', regionChunk)}
            </div>
            {regionChunk.map((region, j) => (
              <div
                key={`${section.title}-${item.key}-${region}-${j}`}
                className="h-4 md:h-7 bg-[#CFD7D7] rounded-md text-[5px] md:text-[16px] flex items-center justify-center"
              >
                {getCount(region, item.key, item.field || 'count')}
              </div>
            ))}
          </React.Fragment>
        ))}
      </div>
    </div>
  </div>
))}
        </div>
      ))}
    </div>
  );
};
const Dropdown = ({ items, selectedItem, onItemSelect }) => {
  const [isOpen, setIsOpen] = useState(false);
  const contentRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [thumbHeight, setThumbHeight] = useState(40);
  const [isDragging, setIsDragging] = useState(false);
  const [startY, setStartY] = useState(0);
  const [startScrollTop, setStartScrollTop] = useState(0);
  const thumbRef = useRef(null);

  const toggleDropdown = () => setIsOpen(!isOpen);
  
  const handleItemClick = (item) => {
    onItemSelect(item);
    setIsOpen(false);
  };

  // Calculate thumb height based on content height
  useEffect(() => {
    const content = contentRef.current;
    if (!content) return;

    const updateThumbHeight = () => {
      const { scrollHeight, clientHeight } = content;
      const visibleRatio = clientHeight / scrollHeight;
      setThumbHeight(Math.max(visibleRatio * clientHeight, 20));
    };

    updateThumbHeight();
    window.addEventListener("resize", updateThumbHeight);
    return () => window.removeEventListener("resize", updateThumbHeight);
  }, [isOpen, items]);

  // Handle scroll events
  const handleScroll = () => {
    const content = contentRef.current;
    if (!content) return;

    const { scrollTop, scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const scrollRatio = scrollTop / maxScrollTop;
    const trackHeight = content.clientHeight - thumbHeight;
    setScrollTop(scrollRatio * trackHeight);
  };

  // Mouse down on thumb
  const handleThumbMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();

    setIsDragging(true);
    setStartY(e.clientY);
    setStartScrollTop(contentRef.current.scrollTop);

    document.addEventListener("mousemove", handleThumbMouseMove);
    document.addEventListener("mouseup", handleThumbMouseUp);
    document.body.style.cursor = "grabbing";
  };

  // Mouse move when dragging thumb
  const handleThumbMouseMove = (e) => {
    if (!isDragging) return;

    const deltaY = e.clientY - startY;
    const content = contentRef.current;
    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    const trackHeight = clientHeight - thumbHeight;

    // Calculate new scroll position
    const scrollDelta = (deltaY / trackHeight) * maxScrollTop;
    const newScrollTop = Math.min(
      Math.max(0, startScrollTop + scrollDelta),
      maxScrollTop
    );

    content.scrollTop = newScrollTop;
  };

  // Mouse up when dragging thumb
  const handleThumbMouseUp = () => {
    setIsDragging(false);
    document.removeEventListener("mousemove", handleThumbMouseMove);
    document.removeEventListener("mouseup", handleThumbMouseUp);
    document.body.style.cursor = "";
  };

  // Click on track to jump to position
  const handleTrackClick = (e) => {
    const content = contentRef.current;
    if (!content || e.target === thumbRef.current) return;

    const track = e.currentTarget;
    const trackRect = track.getBoundingClientRect();
    const clickPosition = e.clientY - trackRect.top;
    const thumbCenter = thumbHeight / 2;
    const scrollRatio =
      (clickPosition - thumbCenter) / (trackRect.height - thumbHeight);

    const { scrollHeight, clientHeight } = content;
    const maxScrollTop = scrollHeight - clientHeight;
    content.scrollTop = scrollRatio * maxScrollTop;
  };

  // Button scroll handlers
  const handleButtonScroll = (direction) => {
    if (contentRef.current) {
      contentRef.current.scrollBy({
        top: direction === 'up' ? -50 : 50,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="relative mt-2">
      <button 
        className="w-full"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        onClick={toggleDropdown}
      >
        <div className="flex items-center md:gap-2 gap-1">
          <p className="font-semibold md:text-[14px] text-[10px] text-[#152C24] mb-1">
            {selectedItem ? `${selectedItem.label} (${selectedItem.value})` : 'LOCATION'}
          </p>
          <img 
            src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747676808/Expand_down_4_qe4gpr.png" 
            alt="Dropdown indicator" 
            className="md:w-[26px] md:h-[26px] h-[12px] w-[12px]"
          />
        </div>
      </button>
      
      {isOpen && (
        <div 
          className="absolute z-10 md:w-[200px] w-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg"
          role="listbox"
        >
          <div className="w-full flex justify-end py-1">
            <button 
              onClick={(e) => {
                e.stopPropagation();
                handleButtonScroll('up');
              }}
              className="p-1"
            >
              <img 
                src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747461105/Expand_up_mflvtu.png" 
                alt="Scroll up" 
                className="w-3 h-3"
              />
            </button>
          </div>

          <div className="custom-scroll-area">
            <div
              className="custom-scroll-content"
              ref={contentRef}
              onScroll={handleScroll}
            >
              {items.map((group) => (
                <div key={group.heading} className="mb-2">
                  <div className="px-3 py-1 bg-gray-100 font-semibold text-black md:text-[14px] text-[10px]">
                    {group.heading}
                  </div>
                  {group.items.map((item) => (
                    <div
                      key={item.value}
                      className={`px-3 py-2 hover:bg-[#03897B]/10 cursor-pointer ${
                        selectedItem?.value === item.value ? 'bg-[#03897B]/10' : ''
                      }`}
                      role="option"
                      onClick={() => handleItemClick(item)}
                    >
                      <div className="md:text-[14px] text-[12px] font-medium text-black">
                        {item.label}
                      </div>
                      <div className="md:text-[11px] text-[9px] text-gray-500">
                        {item.value}
                      </div>
                      <div 
                        className={`w-6 h-6 rounded-full float-right -mt-6 ${
                          selectedItem?.value === item.value ? 'bg-[#152C24]' : 'bg-gray-200'
                        }`}
                      />
                    </div>
                  ))}
                </div>
              ))}
            </div>
            <div className="custom-scroll-track" onClick={handleTrackClick}>
              <div
                ref={thumbRef}
                className="custom-scroll-thumb"
                style={{
                  height: `${thumbHeight}px`,
                  top: `${scrollTop}px`,
                  cursor: isDragging ? "grabbing" : "grab",
                }}
                onMouseDown={handleThumbMouseDown}
              ></div>
            </div>
          </div>

          <div className="w-full flex justify-end py-1">
            <button 
              onClick={(e) => {
                e.stopPropagation();
                handleButtonScroll('down');
              }}
              className="p-1"
            >
              <img 
                src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747461085/Expand_down_vbf4bb.png" 
                alt="Scroll down" 
                className="w-3 h-3"
              />
            </button>
          </div>
        </div>
      )}

      <style jsx>{`
        .custom-scroll-area {
          position: relative;
          max-height: 200px;
          display: flex;
          overflow: hidden;
        }

        .custom-scroll-content {
          flex: 1;
          overflow-y: scroll;
          scrollbar-width: none;
          -ms-overflow-style: none;
          padding-right: 4px;
        }

        .custom-scroll-content::-webkit-scrollbar {
          display: none;
        }

        .custom-scroll-track {
          width: 14px;
          background: #e7ebeb;
          border-radius: 3px;
          position: relative;
          margin-left: 4px;
          cursor: pointer;
        }

        .custom-scroll-thumb {
          width: 100%;
          background: #979797;
          border-radius: 3px;
          position: absolute;
          right: 0;
          transition: background 0.2s;
        }

        .custom-scroll-thumb:hover {
          background: #444;
        }

        @media (max-width: 768px) {
          .custom-scroll-track {
            width: 4px;
            margin-left: 2px;
          }
          .custom-scroll-area {
            position: relative;
            max-height: 150px;
            display: flex;
            overflow: hidden;
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};
const ReportRegistrationPage = () => {
  const location = useLocation(); // <-- Move this inside the component
  const queryParams = new URLSearchParams(location.search);
  const selectedCountry = queryParams.get('country') || 'South Africa'; 
  const [regions, setRegions] = useState([]);
  const regionMap = getRegionMap(selectedCountry);
  const regionDisplayNames = regions;
  const [enterpriseTotal, setEnterpriseTotal] = useState(0);
  const countryMapImages = {
    'Angola': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444884/Angola_Map_qri8d9.jpg',
    'Botswana': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444904/Botswana_Map_ixujke.jpg',
    'Cameroon': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444883/Cameroon_Map_oli1ea.jpg',
    'Côte d\'Ivoire (Ivory Coast)': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444885/Cote_D_Ivorie_Map_titbtu.jpg',
    'Congo (Kinshasa/DRC)': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444894/Congo_DRC_map_eooyiv.jpg',
    'Eswatini': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444890/Eswatini_Map_qg49ik.jpg',
    'Ethiopia': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444894/Ethiopia_map_ijxizh.jpg',
    'Ghana': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444895/Ghana_Map_at9tji.jpg',
    'Kenya': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444894/Kenya_Map_axuptr.jpg',
    'Lesotho': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444894/Lesotho_Map_wwweao.jpg',
    'Malawi': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444895/Malawi_Map_cezzt0.jpg',
    'Mozambique': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444889/Mozambique_map_mr5gti.jpg',
    'Namibia': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444889/Namibia_Map_i2r8th.jpg',
    'Nigeria': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444890/Nigeria_Map_fyxypv.jpg',
    'Rwanda': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750447501/Rwanda_Map_dslxiy.jpg',
    'Senegal': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444882/Senegal_Map_gbxrbb.jpg',
    'Somalia': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444942/Somalia_Map_xwxy02.jpg',
    'South Africa': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444882/South_Africa_map_fhljpi.jpg',
    'South Sudan': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444882/South_Sudan_Map_ox0wyh.jpg',
    'Tanzania': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750447501/Tanzania_map_pwlaay.jpg',
    'Uganda': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444883/Uganda_map_rrjk9p.jpg',
    'Zambia': 'https://res.cloudinary.com/dzngpgki4/image/upload/v1750444885/Zambia_Map_izofpt.jpg'
  };
  useEffect(() => {
    // Update regions when country changes
    const regionMap = getRegionMap(selectedCountry);
    setRegions(Object.keys(regionMap));
  }, [selectedCountry]);

    // Use the same region structure as your dropdown
// Update the REGION_OPTIONS to be dynamic
const REGION_OPTIONS = regions.map(region => ({
  name: region,
  value: getRegionMap(selectedCountry)[region]
}));

  // Create display names array for the EnterpriseSection
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [datePickerFor, setDatePickerFor] = useState(null); // 'opening' or 'closing'
  const [openingDate, setOpeningDate] = useState('');
  const [closingDate, setClosingDate] = useState('');

  const handleDateClick = (type) => {
    setDatePickerFor(type);
    setShowDatePicker(true);
  };
  
  const handleDateApply = ({ date }) => {
    if (datePickerFor === 'opening') {
      setOpeningDate(date);
    } else {
      setClosingDate(date);
    }
  };


  const [challengeStats, setChallengeStats] = useState({
    'ACCESS TO FINANCE': 0,
    'COMPLIANCE': 0,
    'MARKET ACCESS': 0,
    'SKILL GAPS': 0,
    'TECHNOLOGY': 0,
    'CASH FLOW': 0,
    'MENTORSHIP': 0,
    'INSTABILITY': 0,
    'INNOVATION': 0,
    'INFORMATION ACCESS': 0,
    'SECURITY': 0,
    'OTHERS': 0
  });
  
  
  const [countsByRegionAndSMME, setCountsByRegionAndSMME] = useState({});
  const [hoveredItems, setHoveredItems] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [agreed, setAgreed] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    // Modify the stats fetching to ignore cache
const fetchStats = async () => {
  try {
    const statsSnapshot = await getDoc(doc(db, "stats", "registrations"));
    if (statsSnapshot.exists()) {
      setStats(statsSnapshot.data());
    } else {
      console.log("No stats data found - calculating fresh");
      // Trigger fresh calculation
    }
  } catch (error) {
    console.error("Error fetching stats:", error);
  }
};
    fetchStats();
  }, []);
  useEffect(() => {
    const fetchChallengeStats = async () => {
      const statsRef = doc(db, "stats", "challenges");
      
      // Use onSnapshot for real-time updates
      const unsubscribe = onSnapshot(statsRef, (doc) => {
        if (doc.exists()) {
          setChallengeStats(doc.data());
        }
      });
  
      return () => unsubscribe(); // Cleanup on unmount
    };
  
    fetchChallengeStats();
  }, []);
  
  const dropdownItems = [
  {
    heading: "Agriculture and Agribusiness",
    items: [
      { label: "Smallholder farms", value: "SMF" },
      { label: "Agro-processing", value: "AGP" },
      { label: "Agri-input suppliers", value: "AIP" },
      { label: "Horticulture businesses", value: "HCB" },
      { label: "Poultry", value: "POF" },
      { label: "Fish farms", value: "FIF" },
      { label: "Agri-tech startups", value: "ATC" }
    ]
  },
  {
    heading: "Manufacturing and Processing",
    items: [
      { label: "Textile and garment production", value: "TGP" },
      { label: "Furniture making and carpentry", value: "FPC" },
      { label: "Metal fabrication", value: "MEF" },
      { label: "Soap and detergent production", value: "SDP" },
      { label: "Food and beverage processing", value: "FBP" }
    ]
  },
  {
    heading: "Retail and Trade",
    items: [
      { label: "Small shops (dukas/spazas)", value: "SDS" },
      { label: "Market vendors", value: "MAV" },
      { label: "Wholesalers and distributors", value: "WHD" },
      { label: "Online retail platforms", value: "ORP" }
    ]
  },
  {
    heading: "Services",
    items: [
      { label: "Transport and logistics", value: "TRL" },
      { label: "Repair services", value: "RPS" },
      { label: "Beauty and personal care", value: "BPC" },
      { label: "Laundry and dry cleaning", value: "LDC" },
      { label: "Hospitality", value: "HOS" },
      { label: "Event planning and catering", value: "EPC" }
    ]
  },
  {
    heading: "Technology and Innovation (Tech SMEs)",
    items: [
      { label: "Fintech", value: "FIT" },
      { label: "Healthtech", value: "HET" },
      { label: "Edtech", value: "EDT" },
      { label: "Software and app development", value: "SAD" },
      { label: "E-commerce and logistics tech", value: "ECL" }
    ]
  },
  {
    heading: "Construction and Real Estate",
    items: [
      { label: "Small construction firms", value: "SCF" },
      { label: "Real estate agents and developers", value: "RAD" },
      { label: "Hardware and building material suppliers", value: "HBM" }
    ]
  },
  {
    heading: "Creative and Cultural Industries",
    items: [
      { label: "Art and crafts production", value: "ACP" },
      { label: "Music and film production", value: "MFP" },
      { label: "Fashion and design businesses", value: "FDB" },
      { label: "Photography and videography services", value: "PVS" }
    ]
  },
  {
    heading: "Health and Wellness",
    items: [
      { label: "Private clinics and pharmacies", value: "PCP" },
      { label: "Fitness centers and gyms", value: "FCG" },
      { label: "Traditional medicine practitioners", value: "TMP" },
      { label: "Health product distributors", value: "HPD" }
    ]
  },
  {
    heading: "Education and Training",
    items: [
      { label: "Private schools and daycares", value: "PSD" },
      { label: "Vocational training centers", value: "VTC" },
      { label: "Tutoring services", value: "TUS" },
      { label: "Edtech startups", value: "EDS" }
    ]
  }
]
 
const challengesData = [
  {
    id: 1, // Added unique ID
    title: "Limited Access to Finance",
    code: "ACCESS TO FINANCE",
    description: "Many farmers lack access to affordable credit and insurance.",
    solution: "Solution: Develop inclusive agricultural financing, microloans, and mobile banking services tailored to smallholders."
  },
  {
    id: 2,
    title: "Poor Infrastructure",
    code: "COMPLIANCE",
    description: "Inadequate roads, storage, and market facilities hinder distribution and increase post-harvest losses.",
    solution: "Solution: Invest in rural infrastructure, including roads, warehouses, and market access points."
  },
  {
    id: 3,
    title: "Low Access to Technology and Inputs",
    code: "MARKET ACCESS",
    description: "Limited availability of quality seeds, fertilizers, and modern tools.",
    solution: "Solution: Encourage public-private partnerships to improve input supply chains and subsidize access for small-scale farmers."
  },
  {
    id: 4,
    title: "Climate Change & Unpredictable Weather",
    code: "SKILL GAPS",
    description: "Droughts, floods, and changing weather patterns disrupt farming cycles.",
    solution: "Solution: Promote climate-smart agriculture, drought-resistant seeds, and improved weather forecasting tools."
  },
  {
    id: 5,
    title: "Market Access and Price Volatility",
    code: "TECHNOLOGY",
    description: "Farmers struggle to connect with profitable markets and face unstable prices.",
    solution: "Solution: Support farmer cooperatives, develop value chains, and use digital platforms to connect farmers with buyers."
  },
  {
    id: 6,
    title: "Youth Disinterest in Agriculture",
    code: "SKILL GAPS",
    description: "Agriculture is seen as unprofitable and labor-intensive.",
    solution: "Solution: Promote agribusiness, use of technology, and access to land and finance for young entrepreneurs."
  },
  {
    id: 7,
    title: "Pests and Diseases",
    code: "INSTABILITY",
    description: "Crop and livestock pests can wipe out harvests and income.",
    solution: "Solution: Increase research on pest control, and provide early warning systems and access to effective pesticides or biological controls."
  },
  {
    id: 8,
    title: "Land Tenure Insecurity",
    code: "INSTABILITY",
    description: "Many farmers work without formal land rights, discouraging long-term investment.",
    solution: "Solution: Implement land reforms and promote secure, documented land ownership."
  },
  {
    id: 9,
    title: "Lack of skills and technical know-how",
    code: "SKILL GAPS",
    description: "Limits smallholder farmers' productivity and income potential by preventing them from adopting modern, efficient farming practices.",
    solution: "Solution: targeted training, access to extension services, and digital tools tailored to their needs."
  },
  {
    id: 10,
    title: "Crime and insecurity",
    code: "SECURITY",
    description: "Disrupt smallholder farmers' activities by threatening their safety, crops, and livestock, leading to reduced productivity and investment.",
    solution: "Solution: Strengthened rural security, community policing, and support systems to protect livelihoods."
  },
  {
    id: 11,
    title: "Lack of information",
    code: "INFORMATION ACCESS",
    description: "Prevents smallholder farmers from making informed decisions about markets, weather, and best practices, leading to lower yields and income.",
    solution: "Solution: improved access to timely, relevant agricultural information through mobile platforms and extension services."
  },
  {
    id: 12,
    title: "Others",
    code: "OTHERS",
    description: "",
    solution: ""
  }
];
const [reportSelections, setReportSelections] = useState({
  COUNTRY: "-",
  REGION: "-",
  DISTRICT: "-",
  MUNICIPALITY: "-",
  SMMEs: "-",
  "VALUE CHAIN": "-",
  PROJECT: "-",
  "USER TYPE": "-"
});
const [totalEntrepreneurs, setTotalEntrepreneurs] = useState(0);

const handleReportDropdownChange = (type, value) => {
  setReportSelections(prev => {
    const newSelections = {
      ...prev,
      [type]: value
    };
    
    // Reset dependent fields when country changes
    if (type === 'COUNTRY') {
      newSelections.REGION = "-";
      newSelections.DISTRICT = "-";
      newSelections.MUNICIPALITY = "-";
    }
    
    return newSelections;
  });
};

const handleSaveFromDropdown = (savedData) => {
  // Update the report selections with the saved data
  setReportSelections(savedData);
  
  // You may also want to trigger a data refresh here
  // fetchData(savedData);
};

const totalSelections = Object.values(challengeStats).reduce((sum, val) => sum + val, 0);
const challenges = [
  { 
    label: "LIMITED ACCESS TO FINANCE", 
    value: challengeStats['ACCESS TO FINANCE'] || 0,
    percentage: totalSelections > 0 
      ? Math.round((challengeStats['ACCESS TO FINANCE'] / totalSelections * 100))
      : 0
  },
  { 
    label: "POOR INFRASTRUCTURE", 
    value: challengeStats['COMPLIANCE'] || 0,
    percentage: totalSelections > 0 
      ? Math.round((challengeStats['COMPLIANCE'] / totalSelections * 100))
      : 0
  },
  { 
    label: "ACCESS TO TECHNOLOGY & INPUTS", 
    value: challengeStats['MARKET ACCESS'] || 0,
    percentage: totalSelections > 0 
      ? Math.round((challengeStats['MARKET ACCESS'] / totalSelections * 100))
      : 0
  },
  { 
    label: "LIMITED AGRICULTURAL EXTENSION SERVICES", 
    value: challengeStats['SKILL GAPS'] || 0,
    percentage: totalSelections > 0 
      ? Math.round((challengeStats['SKILL GAPS'] / totalSelections * 100))
      : 0
  },
  { 
    label: "MARKET ACCESS AND PRICE VOLATILITY", 
    value: challengeStats['TECHNOLOGY'] || 0,
    percentage: totalSelections > 0 
      ? Math.round((challengeStats['TECHNOLOGY'] / totalSelections * 100))
      : 0
  },
  { 
    label: "CLIMATE CHANGE & WEATHER", 
    value: challengeStats['CASH FLOW'] || 0,
    percentage: totalSelections > 0 
      ? Math.round((challengeStats['CASH FLOW'] / totalSelections * 100))
      : 0
  },
  { 
    label: "YOUTH DISINTERESTED IN AGRICULTURE", 
    value: challengeStats['MENTORSHIP'] || 0,
    percentage: totalSelections > 0 
      ? Math.round((challengeStats['MENTORSHIP'] / totalSelections * 100))
      : 0
  },
  { 
    label: "PEST AND DISEASES", 
    value: challengeStats['INSTABILITY'] || 0,
    percentage: totalSelections > 0 
      ? Math.round((challengeStats['INSTABILITY'] / totalSelections * 100))
      : 0
  },
  { 
    label: "LAND TENURE", 
    value: challengeStats['INNOVATION'] || 0,
    percentage: totalSelections > 0 
      ? Math.round((challengeStats['INNOVATION'] / totalSelections * 100))
      : 0
  },
  { 
    label: "SKILLS AND TECHNICAL KNOW HOW", 
    value: challengeStats['INFORMATION ACCESS'] || 0,
    percentage: totalSelections > 0 
      ? Math.round((challengeStats['INFORMATION ACCESS'] / totalSelections * 100))
      : 0
  },
  { 
    label: "CRIME AND INSECURITY", 
    value: challengeStats['SECURITY'] || 0,
    percentage: totalSelections > 0 
      ? Math.round((challengeStats['SECURITY'] / totalSelections * 100))
      : 0
  },
  { 
    label: "INFORMATION", 
    value: challengeStats['INFORMATION ACCESS'] || 0,
    percentage: totalSelections > 0 
      ? Math.round((challengeStats['INFORMATION ACCESS'] / totalSelections * 100))
      : 0
  },
  { 
    label: "OTHERS", 
    value: challengeStats['OTHERS'] || 0,
    percentage: totalSelections > 0 
      ? Math.round((challengeStats['OTHERS'] / totalSelections * 100))
      : 0
  }
].map(challenge => ({
  ...challenge,
  percentage: `${challenge.percentage}%`
}));
const calculateNormalizedPercentages = () => {
  // Get the total registrations for this country
  const countryTotal = totalEntrepreneurs || 1; // Avoid division by zero
  
  // Create challenge data with proper percentages
  const challengesWithData = challengesData.map(challenge => {
    const count = challengeStats[challenge.code] || 0;
    const percentage = Math.round((count / countryTotal) * 1);
    
    return {
      ...challenge,
      value: count,
      percentage: `${percentage}%`,
      rawPercentage: percentage
    };
  });

  // Find the highest value for highlighting
  const maxValue = Math.max(...challengesWithData.map(c => c.value));
  
  return {
    normalizedChallenges: challengesWithData.map(item => ({
      ...item,
      isHighest: item.value === maxValue && item.value > 0
    })),
    accessToFinancePercentage: challengesWithData.find(c => c.code === "ACCESS TO FINANCE")?.percentage || "0%"
  };
};


const calculateChallengePercentages = () => {
  // Get the total number of entrepreneurs (users)
  const totalUsers = totalEntrepreneurs || 1; // Prevent division by zero
  
  return challengesData.map(challenge => {
    const count = challengeStats[challenge.code] || 0;
    const percentage = Math.round((count / totalUsers) * 100);
    
    return {
      ...challenge,
      value: count,
      percentage: `${percentage}%`,
      rawPercentage: percentage,
      isHighest: false // We'll calculate this separately
    };
  });
};
const { normalizedChallenges, accessToFinancePercentage } = calculateNormalizedPercentages();


useEffect(() => {

// Update the fetchChallengeStats function inside the ReportRegistrationPage component
const fetchChallengeStats = async () => {
  try {
    const statsRef = doc(db, "stats", "challenges");
    const statsDoc = await getDoc(statsRef);
    
    if (statsDoc.exists()) {
      const allStats = statsDoc.data();
      
      // Get the country name properly (handle both string and object cases)
      const countryName = typeof selectedCountry === 'string' ? 
        selectedCountry : 
        selectedCountry.name || 'South Africa';
      
      // Get stats for this country or initialize empty
      const countryStats = allStats[countryName] || {};
      
      // Initialize all challenges with their counts
      const initializedStats = {};
      challengesData.forEach(challenge => {
        initializedStats[challenge.code] = countryStats[challenge.code] || 0;
      });
      
      setChallengeStats(initializedStats);
    } else {
      console.log("No challenge stats document exists - calculating fresh");
      
      // Fallback: Calculate from individual submissions
      const countryName = typeof selectedCountry === 'string' ? 
        selectedCountry : 
        selectedCountry.name || 'South Africa';
      
      const q = query(
        collectionGroup(db, "challengeSubmissions"),
        where("country", "==", countryName)
      );
      
      const querySnapshot = await getDocs(q);
      const calculatedStats = {};
      
      // Initialize all challenge codes
      challengesData.forEach(challenge => {
        calculatedStats[challenge.code] = 0;
      });
      
      // Count all challenge selections
      querySnapshot.forEach((doc) => {
        const submission = doc.data();
        if (submission.challenges) {
          submission.challenges.forEach(challengeId => {
            const challenge = challengesData.find(c => c.id === challengeId);
            if (challenge) {
              calculatedStats[challenge.code] += 1;
            }
          });
        }
      });
      
      setChallengeStats(calculatedStats);
      
      // Update the stats document for future use
      await updateDoc(doc(db, "stats", "challenges"), {
        [countryName]: calculatedStats
      }, { merge: true });
    }
  } catch (error) {
    console.error("Error fetching challenge stats:", error);
  }
};

// Update the useEffect that calls fetchChallengeStats

// Update the calculateNormalizedPercentages function
const calculateNormalizedPercentages = () => {
  // Get the total registrations for this country
  const countryTotal = totalEntrepreneurs || 1; // Avoid division by zero
  
  // Create challenge data with proper percentages
  const challengesWithData = challengesData.map(challenge => {
    const count = challengeStats[challenge.code] || 0;
    const percentage = Math.round((count / countryTotal) * 1);
    
    return {
      ...challenge,
      value: count,
      percentage: `${percentage}%`,
      rawPercentage: percentage
    };
  });

  // Find the highest value for highlighting
  const maxValue = Math.max(...challengesWithData.map(c => c.value));
  
  return {
    normalizedChallenges: challengesWithData.map(item => ({
      ...item,
      isHighest: item.value === maxValue && item.value > 0
    })),
    accessToFinancePercentage: challengesWithData.find(c => c.code === "ACCESS TO FINANCE")?.percentage || "0%"
  };
};

// Update the challenges rendering section

// Update the useEffect that calls fetchChallengeStats

// Update the calculateNormalizedPercentages function

// Update the challenges rendering section
const { normalizedChallenges, accessToFinancePercentage } = calculateNormalizedPercentages();
  
  
  fetchChallengeStats();
}, [selectedCountry]);
const updateStats = async (country, region, smmeType, landSize) => {
  const statsRef = doc(db, "stats", "registrations");
  
  try {
    await runTransaction(db, async (transaction) => {
      const statsDoc = await transaction.get(statsRef);
      let statsData = statsDoc.exists() ? statsDoc.data() : {};
      
      // Initialize country if not exists
      if (!statsData[country]) {
        statsData[country] = {};
      }
      
      // Initialize region if not exists
      if (!statsData[country][region]) {
        statsData[country][region] = {
          total: 0,
          landSizeTotal: 0,
          bySmmetype: {}
        };
      }
      
      // Initialize SMME type if not exists
      if (!statsData[country][region].bySmmetype[smmeType]) {
        statsData[country][region].bySmmetype[smmeType] = {
          count: 0,
          landSizeTotal: 0
        };
      }
      
      // Update counts - INCREMENT rather than set
      statsData[country][region].total += 1;
      statsData[country][region].landSizeTotal += landSize;
      statsData[country][region].bySmmetype[smmeType].count += 1;
      statsData[country][region].bySmmetype[smmeType].landSizeTotal += landSize;
      
      transaction.set(statsRef, statsData);
    });
  } catch (error) {
    console.error("Transaction failed:", error);
  }
};

const totalPercentage = challenges.reduce((sum, challenge) => {
  // Extract numeric value (remove % sign)
  const percent = parseInt(challenge.percentage) || 0;
  return sum + percent;
}, 0);

// Round to nearest whole number for display

const renderButtonWithIcon = (text, bgColor, iconColor, isLarge = false) => {
  // Define color mappings
  const bgColorClasses = {
    "#152C24": "bg-[#152C24]",
    "#2E453D": "bg-[#2E453D]",
    "#FDBB26": "bg-[#FDBB26]",
  };

  const iconColorClasses = {
    "#152C24": "bg-[#152C24]",
    "#2E453D": "bg-[#2E453D]",
    "#FDBB26": "bg-[#FDBB26]",
  };

  return (
    <div className="flex items-center">
      {/* Button Text */}
      <div className={`${bgColorClasses[bgColor]} text-white md:text-[16px] text-[5px] px-2 md:px-4 ${
        isLarge ? "md:py-[14px] py-[3px]" : "md:py-[14px] py-[3px]"
      } md:rounded-l-md rounded-l-md font-bold md:w-[300px] whitespace-nowrap flex items-center h-full`}>
        {text}
      </div>
      
      {/* Desktop Icon (visible on md and up) */}
      <div className={`${iconColorClasses[iconColor]} hidden md:flex items-center md:py-2  justify-center md:w-[57px] md:h-[52px] rounded-r-md`}>
        <img
          src="https://res.cloudinary.com/dzngpgki4/image/upload/v1744139834/Expand_right_2_a4wodr.png"
          alt="Expand"
          className="w-[20px] h-[6px] md:h-[40px] md:w-[55px]  object-contain"
        />
      </div>
      
      {/* Mobile Icon (visible below md) */}
      <div className={`${iconColorClasses[iconColor]} md:hidden flex items-center justify-center w-[20px]  h-[14px] rounded-r-md`}>
        <img
          src="https://res.cloudinary.com/dzngpgki4/image/upload/v1744370865/Expand_right_5_l2cjmf.png"
          alt="Expand"
          className="w-[20px] h-[6px]  object-contain"
        />
      </div>
    </div>
  );
};

  return (
    <div className="bg-white">
      <div className="p-6 md:px-24 px-4">
        {/* Header Section */}
        <div className="flex justify-between items-center">
          <h2 className="text-[14px] md:text-[36px] font-bold tracking-wide text-[#152C24]">
            REPORTS
          </h2>
        </div>

        {/* Search and Subscribe Section */}
        <div className="flex justify-between items-center md:mt-6 mt-2">
        <div>
<img src="https://res.cloudinary.com/dzngpgki4/image/upload/v1748292859/FarmNet_Logo_Green_4_eulwfl.png" alt=".." className="md:w-[250px] md:h-[40px] w-[80px] h-[15px]"/>
        </div>
          <div className="flex md:space-x-6">
            <div>
              <div className="flex items-center flex-shrink-0 md:mt-0 md:pr-0 pr-3 ">
                <input 
                  className="md:text-[16px] text-[5px] placeholder-white border-[#2E453D] outline-none bg-[#2E453D] text-white md:px-4 px-2 md:w-[300px] md:py-[16px] py-[5px] md:rounded-tl-md rounded-tl-md font-bold md:rounded-bl-md rounded-bl-md whitespace-nowrap h-full" 
                  placeholder="XXXXXXXXXX"
                />
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1744139834/Expand_right_2_a4wodr.png"
                  alt="Expand"
                  className="w-[20px] h-[6px] md:h-[51px] md:w-[55px] md:flex hidden object-contain md:rounded-tr-md rounded-tr-md font-bold md:rounded-br-md bg-[#152C24] px-2 md:py-2"
                />
                <img
                  src="https://res.cloudinary.com/dzngpgki4/image/upload/v1744370865/Expand_right_5_l2cjmf.png"
                  alt="Expand"
                  className="w-[20px] h-[16px] md:hidden py-[3px] flex md:h-[51px] md:w-[57px] object-contain md:rounded-tr-xl rounded-tr-md font-bold md:rounded-br-md rounded-br-md bg-[#152C24] md:px-2 px-[2px] md:py-1"
                />
              </div>
              <p className="md:text-[14px] text-[4px] font-normal mt-1">Insert user Id number to view reports</p>
            </div>
            <div>
              {renderButtonWithIcon("SUBSCRIBE", "#152C24", "#FDBB26", true)}
              <p className="md:text-[14px] text-[4px]  font-normal mt-1">Click to subscribe for reports</p>
            </div>
          </div>
        </div>

      
        {/* Reports Title */}
        <div className="md:mt-5 mt-3">
          <h2 className="md:text-[36px] text-[12px] text-[#152C24] font-bold">
            <span className="text-[#979797]">Reports | </span>{selectedCountry}
          </h2>
        </div>

        {/* Date and Run Report Section */}
        <div className="flex justify-between items-center md:mb-4 md:-mt-0 ">
        <div className="flex flex-row md:space-y-0 md:space-x-10 text-[#19253C] md:mt-10 mt-2">
  <div className="flex md:space-x-4 space-x-1 items-center cursor-pointer" 
    onClick={() => handleDateClick('opening')}>
    <h1 className="md:text-[20px] text-[8px] font-semibold">
      Opening Date {openingDate && `: ${openingDate}`}
    </h1>
    <img 
      src="https://res.cloudinary.com/dzngpgki4/image/upload/v1745016720/image_1686_xak1hv.png" 
      alt="calendar" 
      className="md:w-[22px] md:h-[22px] h-[15px]" 
    />
  </div>
  <div className="flex md:space-x-4 space-x-1 items-center cursor-pointer"
    onClick={() => handleDateClick('closing')}>
    <h1 className="md:text-[20px] text-[8px] font-semibold">
      Closing Date {closingDate && `: ${closingDate}`}
    </h1>
    <img 
      src="https://res.cloudinary.com/dzngpgki4/image/upload/v1745016720/image_1686_xak1hv.png" 
      alt="calendar" 
      className="md:w-[22px] md:h-[22px] h-[15px]" 
    />
  </div>

  {showDatePicker && (
    <DateRangePopup
      onClose={() => setShowDatePicker(false)}
      onApply={handleDateApply}
      dateType={datePickerFor}
    />
  )}
</div>
          <a href="/ViewMoreReport">
          <div>
            {renderButtonWithIcon("VIEW MORE REPORT", "#2E453D", "#152C24")}
            <p className="md:text-[14px] text-[4px] font-normal mt-1">Click to run reports selected</p>
          </div>
          </a>
        </div>

<h1 className="font-bold text-[8px] md:text-[20px] text-[#152C24] md:mt-14 mt-5">
  SURVEY ABOUT CHALLENGES OF ENTERPRENEURSHIP
</h1>

<div className="flex flex-col md:flex-row justify-between items-start md:mt-10 mt-4 gap-2">
  <div>
    <h1 className="font-bold text-[8px] md:text-[20px] md:w-[190px] text-[#152C24] md:mb-8">Challenges</h1>
    <h1 className="font-bold text-[8px] md:text-[20px] md:w-[190px] text-[#152C24] md:mb-8">--</h1>
    <h1 className="font-bold text-[8px] md:text-[20px] md:w-[190px] text-[#152C24]">--</h1>
  </div>

  <div className="grid grid-cols-5 md:grid-cols-5 gap-[1px] md:gap-[3px] w-full">
  {normalizedChallenges.map((item, index) => {
    const isHighest = item.value === Math.max(...normalizedChallenges.map(c => c.value));
  
  return (
    <div 
      key={index} 
      className="relative"
      onMouseEnter={() => setHoveredItems(prev => ({...prev, [index]: true}))}
      onMouseLeave={() => setHoveredItems(prev => ({...prev, [index]: false}))}
    >
      <div className="flex flex-col">
        <div className="flex items-center w-full mb-1">
          <div 
            className={`w-[50px] md:w-[170px] bg-[#CFD7D7] text-black font-normal flex md:px-2 px-1 items-center rounded-tl-[5px] h-[20px] md:h-[45px] ${
              isHighest ? 'border-b-[4px] border-[#FDBB26]' : ''
            }`}
          >
            <span className="text-[4px] md:text-[10px] w-full">{item.title}</span>
          </div>
          <div 
            className={`w-[20px] md:w-[60px] bg-[#152C24] text-white flex items-center justify-center rounded-tr-[5px] md:px-2 px-1 h-[20px] md:h-[45px] ${
              isHighest ? 'border-b-[4px] border-[#FDBB26]' : ''
            }`}
          >
            <span className="text-[4px] md:text-[12px]">{item.percentage}</span>
          </div>
        </div>
        
        <div className={`
          w-full bg-[#E7EBEB] rounded-b-[5px]
          transition-all duration-300 ease-in-out
          overflow-hidden text-black
          ${hoveredItems[index] ? 'max-h-[300px] py-5 px-2' : 'max-h-0 py-0 px-2'}
        `}>
          <p className="text-[4px] text-black md:text-[12px]">{item.description}</p>
          <p className="text-[4px] text-black md:text-[12px]">{item.solution}</p>
        </div>
      </div>
    </div>
  );
})}
  </div>
</div>

        {/* Access to Finance Section */}
        <div className="md:mt-5 mt-3 bg-white flex items-center justify-between">
  <div className="md:text-[20px] text-[8px] text-black">
    <div className="flex items-center md:gap-2 gap-1">
      <p className="font-bold">
        {normalizedChallenges.find(c => c.isHighest)?.title || "Limited Access to Finance"}
      </p>
    </div>
    <ul className="md:mt-4">
      {normalizedChallenges.find(c => c.isHighest)?.description || "Many farmers lack access to affordable credit and insurance."}
      <br />
      {normalizedChallenges.find(c => c.isHighest)?.solution || "Solution: Develop inclusive agricultural financing, microloans, and mobile banking services tailored to smallholders"}
    </ul>
  </div>
  <div>
    <div className="bg-[#152C24] md:w-[200px] h-[40px] md:h-[95px] w-[50px] rounded-[5px] flex items-center justify-center md:mt-4 mt-2">
      <h1 className="md:text-[36px] text-[8px] font-bold text-white">
        {normalizedChallenges.find(c => c.isHighest)?.percentage || accessToFinancePercentage}
      </h1>
    </div>
  </div>
</div>
        <div className='md:h-[2px] h-[1px] bg-black mt-3 md:mt-5'></div>

        {/* Enterprise Section - Now shows real data */}
        <EnterpriseSection 
  regions={Object.keys(getRegionMap(reportSelections.COUNTRY))} 
  country={reportSelections.COUNTRY}
  reportSelections={reportSelections}
  onTotalCalculated={setEnterpriseTotal}
/>

        {/* Location Dropdown */}
        <div className="mb-3 md:mt-6 mt-3">
          <Dropdown 
            items={dropdownItems} 
            selectedItem={selectedItem} 
            onItemSelect={setSelectedItem} 
          />
          
          <div className='md:h-[2.5px] h-[1px] bg-black/50'></div>
          
          <div className="gap-y-1 md:gap-x-2 gap-x-1 md:mt-3 mt-1 items-center">
            <h1 className="md:text-[14px] text-[8px] text-black">
              Select type of enterprise and see mapping of their locations
            </h1>
          </div>
        </div>

        {/* Map Section */}
        <div className="relative w-full">
  <img
    src={
      countryMapImages[reportSelections.COUNTRY] || 
      countryMapImages[selectedCountry?.name] || 
      "https://res.cloudinary.com/dzngpgki4/image/upload/v1750444882/South_Africa_map_fhljpi.jpg"
    }
    alt="Map"
    className="w-full h-full"
  />

  {/* Map Icons - you might want to adjust positions for different country maps */}
  <img
    src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747680890/image_1754_i5w9rd.png"
    alt="Map Icon"
    className="absolute top-[8%] left-[20%] w-5 md:w-28"
  />
  <img
    src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747680890/image_1754_i5w9rd.png"
    alt="Map Icon"
    className="absolute top-[16%] left-[42%] w-5 md:w-28"
  />
  <img
    src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747680890/image_1754_i5w9rd.png"
    alt="Map Icon"
    className="absolute top-[26%] right-[34%] w-5 md:w-28"
  />
  <img
    src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747680890/image_1754_i5w9rd.png"
    alt="Map Icon"
    className="absolute top-[46%] left-[32%] w-5 md:w-28"
  />
  <img
    src="https://res.cloudinary.com/dzngpgki4/image/upload/v1747680890/image_1754_i5w9rd.png"
    alt="Map Icon"
    className="absolute top-[54%] left-[16%] w-5 md:w-28"
  />
</div>
                     
    

        {/* Terms and Conditions */}
        <div className="flex gap-4 items-center mt-5">
          <div
            className="md:w-16 md:h-[63px] w-6 h-[30px] rounded-[5px] bg-[#D9D9D9] flex items-center justify-center cursor-pointer transition"
            onClick={() => setAgreed(!agreed)}
            tabIndex={0}
            aria-pressed={agreed}
            aria-label="Confirm terms and conditions"
          >
            {agreed ? (
              <svg
                width="54"
                height="54"
                viewBox="0 0 54 54"
                fill="none"
                className="md:w-[54px] md:h-[54px] w-[20px] h-[20px] transition-all duration-150"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="27" cy="27" r="27" fill="#01897B" />
                <path
                  d="M39 21L24.75 35L15 25.4545"
                  stroke="white"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            ) : (
              <svg
                width="54"
                height="54"
                viewBox="0 0 54 54"
                fill="none"
                className="md:w-[54px] md:h-[54px] w-[20px] h-[20px] transition-all duration-150"
                xmlns="http://www.w3.org/2000/svg"
              >
                <circle cx="27" cy="27" r="27" fill="#222" />
                <path
                  d="M39 21L24.75 35L15 25.4545"
                  stroke="white"
                  strokeWidth="4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            )}
          </div>
          <p className="md:text-[20px] text-[6px]">
            By ticking I confirm that I agree with the{" "}
            <a className="underline hover:text-teal-600" href="/TermsAndCondition">
              Terms and conditions
            </a>
            ,<a href="/DataPolicy underline">Data </a>and{" "}
            <a className="underline hover:text-teal-600" href="/PrivacyPolicy">
              Privacy policy
            </a>
            .
          </p>
        </div>

        {/* Subscribe Button */}
        <div className="flex justify-end">
          <div>
            {renderButtonWithIcon("DOWNLOAD REPORTS", "#152C24", "#FDBB26", true)}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportRegistrationPage;